import React, {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";

import axios from "axios";

import {
    FaArrowDown,
    FaArrowUp,
    FaCalendarAlt,
    FaChevronLeft,
    FaChevronRight,
    FaDownload,
    FaEdit,
    FaExchangeAlt,
    FaMoneyBillWave,
    FaPlus,
    FaFilter,
    FaReceipt,
    FaSearch,
    FaSyncAlt,
    FaTrash,
    FaWallet,
} from "react-icons/fa";

import "./AccountsExpense.css";


/* ============================================================
   API
============================================================ */

const API_BASE_URL =
    process.env.REACT_APP_API_URL ||
    "https://dgps.co.in";


/*
 * IMPORTANT:
 *
 * Ei route-gulo tomar CI3 AccountExpense controller-er
 * route-er sathe exactly match korte hobe.
 *
 * Example:
 * /api/super-admin/accounts-expense/list
 *
 * Jodi tomar route different hoy,
 * shudhu ei API object-ta change korbe.
 */

const API = {

    // Accounts
    list:
        `${API_BASE_URL}/api/super-admin/accounts`,

    // Add balance
    addBalance:
        `${API_BASE_URL}/api/super-admin/accounts/add-balance`,

    // Expense
    expense:
        `${API_BASE_URL}/api/super-admin/accounts/expense`,

    // Non Expense
    nonExpense:
        `${API_BASE_URL}/api/super-admin/accounts/non-expense`,

    // Transactions
    transactions:
        `${API_BASE_URL}/api/super-admin/accounts/transactions`,

};


/* ============================================================
   HEADERS
============================================================ */

const getHeaders = () => {

    const token =
        localStorage.getItem("token") ||
        localStorage.getItem("auth_token") ||
        localStorage.getItem("access_token") ||
        "";

    return {

        Authorization:
            token
                ? `Bearer ${token}`
                : "",

        Accept:
            "application/json",

        "Content-Type":
            "application/json",

    };

};


/* ============================================================
   MONEY
============================================================ */

const money = (
    value
) => {

    const number =
        Number(value || 0);

    return new Intl.NumberFormat(
        "en-IN",
        {
            style:
                "currency",

            currency:
                "INR",

            minimumFractionDigits:
                2,

            maximumFractionDigits:
                2,
        }
    ).format(number);

};


/* ============================================================
   NUMBER
============================================================ */

const numberValue = (
    value
) => {

    const number =
        Number(value || 0);

    return Number.isFinite(number)
        ? number
        : 0;

};


/* ============================================================
   TODAY
============================================================ */

const getToday = () => {

    const date =
        new Date();

    const year =
        date.getFullYear();

    const month =
        String(
            date.getMonth() + 1
        ).padStart(2, "0");

    const day =
        String(
            date.getDate()
        ).padStart(2, "0");

    return `${year}-${month}-${day}`;

};


/* ============================================================
   INITIAL FORM
============================================================ */

const EMPTY_FORM = {

    account_id:
        "",

    amount:
        "",

    credit_type:
        "",

    expense_type:
        "",

    remarks:
        "",

    transaction_date:
        getToday(),

};
/* ============================================================
   ACCOUNT ID HELPER
============================================================ */

const getAccountId = (account) => {
    if (!account) {
        return "";
    }

    const id =
        account.id ??
        account.account_id ??
        account.accountId ??
        account.ID ??
        account.accountID ??
        "";

    return id !== null &&
        id !== undefined &&
        String(id).trim() !== ""
        ? String(id)
        : "";
};


/* ============================================================
   MAIN COMPONENT
============================================================ */

const AccountsExpense = () => {


    /* ==========================================================
       STATE
    ========================================================== */

    const [
        accounts,
        setAccounts
    ] =
        useState([]);


    const [
        transactions,
        setTransactions
    ] =
        useState([]);


    const [
        loading,
        setLoading
    ] =
        useState(true);


    const [
        saving,
        setSaving
    ] =
        useState(false);


    const [
        error,
        setError
    ] =
        useState("");


    const [
        success,
        setSuccess
    ] =
        useState("");


    const [
        search,
        setSearch
    ] =
        useState("");


    const [
        transactionType,
        setTransactionType
    ] =
        useState("all");
    const [
        appliedFromDate,
        setAppliedFromDate
    ] =
        useState(getToday());

    const [
        appliedToDate,
        setAppliedToDate
    ] =
        useState(getToday());

    const [
        appliedTransactionType,
        setAppliedTransactionType
    ] =
        useState("all");

    const [
        appliedSearch,
        setAppliedSearch
    ] =
        useState("");

    const [
        fromDate,
        setFromDate
    ] =
        useState(getToday());


    const [
        toDate,
        setToDate
    ] =
        useState(getToday());


    const [
        currentPage,
        setCurrentPage
    ] =
        useState(1);

    const [
        transactionCategories,
        setTransactionCategories
    ] =
        useState([
            "Bank Deposit",
            "Cash Deposit",
            "Sales",
            "Opening Balance",
            "Other"
        ]);
    const [
        form,
        setForm
    ] =
        useState(EMPTY_FORM);


    const [
        modal,
        setModal
    ] =
        useState(null);


    const [
        selectedAccount,
        setSelectedAccount
    ] =
        useState(null);


    const [
        editingTransaction,
        setEditingTransaction
    ] =
        useState(null);


    const [
        deleteId,
        setDeleteId
    ] =
        useState(null);


    const rowsPerPage =
        10;


    /* ==========================================================
       LOAD DATA
    ========================================================== */

    const loadData =
        useCallback(
            async () => {

                try {

                    setLoading(true);
                    setError("");


                    /* =====================================================
                       LOAD ACCOUNTS + TRANSACTIONS TOGETHER
                    ===================================================== */

                    const [
                        accountsResponse,
                        transactionsResponse
                    ] = await Promise.all([

                        axios.get(
                            API.list,
                            {
                                headers:
                                    getHeaders(),
                            }
                        ),

                        axios.get(
                            API.transactions,
                            {
                                headers:
                                    getHeaders(),

                                params: {

                                    from_date:
                                        appliedFromDate,

                                    to_date:
                                        appliedToDate,

                                },
                            }
                        ),

                    ]);


                    /* =====================================================
                       ACCOUNTS RESPONSE
                    ===================================================== */

                    const accountsPayload =
                        accountsResponse?.data;


                    if (
                        accountsPayload?.status === false
                    ) {

                        throw new Error(
                            accountsPayload?.message ||
                            "Unable to load accounts."
                        );

                    }


                    const accountsData =
                        accountsPayload?.data ||
                        accountsPayload ||
                        {};


                    const accountRows =
                        Array.isArray(
                            accountsData.accounts
                        )
                            ? accountsData.accounts
                            : Array.isArray(
                                accountsData
                            )
                                ? accountsData
                                : [];


                    /* =====================================================
                       TRANSACTIONS RESPONSE
                    ===================================================== */

                    const transactionsPayload =
                        transactionsResponse?.data;


                    if (
                        transactionsPayload?.status === false
                    ) {

                        throw new Error(
                            transactionsPayload?.message ||
                            "Unable to load transactions."
                        );

                    }


                    const transactionsData =
                        transactionsPayload?.data ||
                        transactionsPayload ||
                        {};


                    const transactionRows =
                        Array.isArray(
                            transactionsData.transactions
                        )
                            ? transactionsData.transactions
                            : Array.isArray(
                                transactionsData
                            )
                                ? transactionsData
                                : [];


                    /* =====================================================
                       SET STATE
                    ===================================================== */

                    setAccounts(
                        accountRows
                    );


                    setTransactions(
                        transactionRows
                    );


                    /* =====================================================
                       DEBUG
                    ===================================================== */

                    console.log(
                        "ACCOUNTS:",
                        accountRows
                    );

                    console.log(
                        "TRANSACTIONS:",
                        transactionRows
                    );


                } catch (err) {

                    console.error(
                        "Accounts & Expense load error:",
                        err
                    );


                    setError(

                        err?.response?.data
                            ?.message ||

                        err?.response?.data
                            ?.error ||

                        err?.message ||

                        "Unable to load Accounts & Expense data."

                    );

                } finally {

                    setLoading(false);

                }

            },

            [
                appliedFromDate,
                appliedToDate
            ]

        );

    useEffect(
        () => {

            loadData();

        },
        [loadData]
    );


    /* ==========================================================
       SUCCESS MESSAGE
    ========================================================== */

    const showSuccess =
        (message) => {

            setSuccess(
                message
            );

            setTimeout(
                () => {
                    setSuccess("");
                },
                3000
            );

        };

    const openModal = (
        type,
        account = null,
        transaction = null
    ) => {

        setError("");
        setSuccess("");

        let accountId = "";

        // ==========================================
        // 1. CLICKED ACCOUNT THEKE ID
        // ==========================================

        if (account) {
            accountId = getAccountId(account);
        }

        // ==========================================
        // 2. EDIT TRANSACTION HOLE ACCOUNT ID
        // ==========================================

        if (!accountId && transaction) {
            accountId =
                transaction.account_id ??
                transaction.accountId ??
                transaction.account?.id ??
                transaction.account?.account_id ??
                "";
        }

        // ==========================================
        // 3. FINAL ACCOUNT FIND
        // ==========================================

        let finalAccount = account;

        if (!finalAccount && accountId) {

            finalAccount =
                accounts.find(
                    item =>
                        String(getAccountId(item)) ===
                        String(accountId)
                ) || null;
        }

        // ==========================================
        // 4. ACCOUNT ID ALWAYS STRING
        // ==========================================

        const finalAccountId =
            String(
                getAccountId(finalAccount) ||
                accountId ||
                ""
            );

        console.log(
            "========== OPEN TRANSACTION =========="
        );

        console.log("TYPE:", type);
        console.log("ACCOUNT:", finalAccount);
        console.log("ACCOUNT ID:", finalAccountId);

        // ==========================================
        // 5. SELECTED ACCOUNT
        // ==========================================

        setSelectedAccount(finalAccount);

        setEditingTransaction(transaction);

        // ==========================================
        // 6. FORM
        // ==========================================

        setForm({

            ...EMPTY_FORM,

            account_id:
                finalAccountId,

            amount:
                transaction?.amount ??
                "",

            credit_type:
                transaction?.credit_type ??
                transaction?.expense_type ??
                "",

            expense_type:
                transaction?.expense_type ??
                "",

            remarks:
                transaction?.remarks ??
                "",

            transaction_date:
                transaction?.transaction_date ??
                transaction?.date ??
                getToday(),

        });

        // ==========================================
        // 7. OPEN MODAL
        // ==========================================

        setModal(type);
    };

    /* ==========================================================
       CLOSE MODAL
    ========================================================== */

    const closeModal =
        () => {

            if (saving) {
                return;
            }

            setModal(null);

            setSelectedAccount(null);

            setEditingTransaction(null);

            setForm(
                EMPTY_FORM
            );

        };


    /* ==========================================================
       FORM CHANGE
    ========================================================== */

    const handleChange =
        (event) => {

            const {
                name,
                value
            } =
                event.target;


            setForm(
                previous => ({
                    ...previous,
                    [name]:
                        value,
                })
            );

        };


    /* ==========================================================
     SAVE TRANSACTION
  ========================================================== */

    const submitTransaction = async (
        event
    ) => {

        event.preventDefault();


        /*
         * IMPORTANT:
         *
         * First priority:
         * selectedAccount.id
         *
         * Fallback:
         * selectedAccount.account_id
         *
         * Last fallback:
         * form.account_id
         */

        const accountId =
            form.account_id ||
            getAccountId(selectedAccount) ||
            "";


        const amount =
            numberValue(
                form.amount
            );


        /* ========================================================
           VALIDATION
        ======================================================== */

        const requestData = {

            account_id:
                Number(accountId),

            amount:
                Number(amount),

            remarks:
                form.remarks || "",

            transaction_date:
                form.transaction_date ||
                getToday(),

        };

        if (amount <= 0) {

            setError(
                "Please enter a valid amount."
            );

            return;

        }


        try {

            setSaving(true);

            setError("");


            /* ======================================================
               SELECT API
            ====================================================== */

            let url =
                API.addBalance;


            if (
                modal ===
                "expense"
            ) {

                url =
                    API.expense;

            }


            if (
                modal ===
                "non-expense"
            ) {

                url =
                    API.nonExpense;

            }


            /* ======================================================
               REQUEST DATA
            ====================================================== */

            const requestData = {

                account_id:
                    accountId,

                amount:
                    amount,

                credit_type:
                    modal === "balance"
                        ? form.credit_type || ""
                        : "",

                expense_type:
                    modal === "expense" || modal === "non-expense"
                        ? form.expense_type || ""
                        : "",

                remarks:
                    form.remarks || "",

                transaction_date:
                    form.transaction_date ||
                    getToday(),

            };

            /*
             * Edit korle transaction ID pathabo.
             */
            if (
                editingTransaction?.id
            ) {

                requestData.transaction_id =
                    editingTransaction.id;

            }


            console.log(
                "ACCOUNT TRANSACTION REQUEST:",
                {
                    url,
                    account_id:
                        accountId,
                    amount,
                    type:
                        modal,
                }
            );


            /* ======================================================
               API REQUEST
            ====================================================== */

            const response =
                await axios.post(

                    url,

                    requestData,

                    {
                        headers:
                            getHeaders(),
                    }

                );


            /* ======================================================
               API ERROR
            ====================================================== */

            if (
                response?.data?.status ===
                false
            ) {

                throw new Error(
                    response?.data?.message ||
                    "Transaction failed."
                );

            }


            /* ======================================================
               SUCCESS
            ====================================================== */

            closeModal();


            showSuccess(

                modal ===
                    "expense"

                    ? "Expense added successfully."

                    : modal ===
                        "non-expense"

                        ? "Non-expense added successfully."

                        : "Balance added successfully."

            );


            /*
             * Account balance + transaction list
             * abar load hobe.
             */

            await loadData();


        } catch (err) {

            console.error(
                "Transaction save error:",
                err
            );


            setError(

                err?.response?.data
                    ?.message ||

                err?.response?.data
                    ?.error ||

                err?.message ||

                "Unable to save transaction."

            );

        } finally {

            setSaving(false);

        }

    };


    /* ==========================================================
       DELETE TRANSACTION
    ========================================================== */

    const deleteTransaction =
        async () => {

            if (!deleteId) {
                return;
            }


            try {

                setSaving(true);

                setError("");


                const response =
                    await axios.post(

                        API.deleteTransaction,

                        {
                            id:
                                deleteId,
                        },

                        {
                            headers:
                                getHeaders(),
                        }

                    );


                if (
                    response?.data?.status === false
                ) {

                    throw new Error(
                        response?.data?.message ||
                        "Unable to delete transaction."
                    );

                }


                setDeleteId(
                    null
                );


                showSuccess(
                    "Transaction deleted successfully."
                );


                await loadData();


            } catch (err) {

                console.error(
                    "Delete transaction error:",
                    err
                );


                setError(
                    err?.response?.data?.message ||
                    err?.message ||
                    "Unable to delete transaction."
                );

            } finally {

                setSaving(false);

            }

        };


    /* ==========================================================
       FILTER TRANSACTIONS
    ========================================================== */

    const normalizeTransactionType = (row) => {

        return String(
            row?.type ||
            row?.transaction_type ||
            ""
        )
            .toLowerCase()
            .trim()
            .replace(/-/g, "_");

    };


    const filteredTransactions =
        useMemo(
            () => {

                let rows =
                    Array.isArray(transactions)
                        ? [...transactions]
                        : [];


                /* DATE FILTER */

                if (appliedFromDate) {

                    rows =
                        rows.filter(
                            row => {

                                const date =
                                    String(
                                        row.transaction_date ||
                                        row.date ||
                                        ""
                                    ).substring(0, 10);

                                return (
                                    !date ||
                                    date >= appliedFromDate
                                );

                            }
                        );

                }


                if (appliedToDate) {

                    rows =
                        rows.filter(
                            row => {

                                const date =
                                    String(
                                        row.transaction_date ||
                                        row.date ||
                                        ""
                                    ).substring(0, 10);

                                return (
                                    !date ||
                                    date <= appliedToDate
                                );

                            }
                        );

                }


                /* TYPE FILTER */

                if (
                    appliedTransactionType !==
                    "all"
                ) {

                    rows =
                        rows.filter(
                            row => {

                                const type =
                                    normalizeTransactionType(
                                        row
                                    );

                                return (
                                    type ===
                                    appliedTransactionType
                                );

                            }
                        );

                }


                /* SEARCH FILTER */

                const keyword =
                    appliedSearch
                        .trim()
                        .toLowerCase();


                if (keyword) {

                    rows =
                        rows.filter(
                            row => {

                                const text = [

                                    row.account_name,

                                    row.account,

                                    row.remarks,

                                    row.transaction_type,

                                    row.type,

                                    row.expense_type,

                                    row.credit_expense_type,

                                    row.category_name,

                                    row.txn_id,

                                    row.transaction_id,

                                ]
                                    .filter(Boolean)
                                    .join(" ")
                                    .toLowerCase();


                                return text.includes(
                                    keyword
                                );

                            }
                        );

                }


                return rows;

            },
            [
                transactions,
                appliedFromDate,
                appliedToDate,
                appliedTransactionType,
                appliedSearch,
            ]
        );


    /* ==========================================================
       RESET PAGE WHEN FILTER CHANGES
    ========================================================== */

    //   useEffect(
    //     () => {

    //       setCurrentPage(
    //         1
    //       );

    //     },
    //     [
    //       search,
    //       fromDate,
    //       toDate,
    //       transactionType,
    //     ]
    //   );


    /* ==========================================================
       PAGINATION
    ========================================================== */

    const totalPages =
        Math.max(
            1,
            Math.ceil(
                filteredTransactions.length /
                rowsPerPage
            )
        );


    const visibleTransactions =
        filteredTransactions.slice(
            (
                currentPage -
                1
            ) *
            rowsPerPage,

            currentPage *
            rowsPerPage
        );


    /* ==========================================================
       ACCOUNT TOTALS
    ========================================================== */

    const totalBalance =
        useMemo(
            () => {

                return accounts.reduce(
                    (
                        total,
                        account
                    ) =>
                        total +
                        numberValue(
                            account.balance ??
                            account.current_balance ??
                            0
                        ),
                    0
                );

            },
            [accounts]
        );


    const totalExpense =
        useMemo(
            () => {

                return transactions.reduce(
                    (
                        total,
                        row
                    ) => {

                        const type =
                            String(
                                row.type ||
                                row.transaction_type ||
                                ""
                            ).toLowerCase();


                        if (
                            type ===
                            "expense"
                        ) {

                            return (
                                total +
                                numberValue(
                                    row.amount
                                )
                            );

                        }


                        return total;

                    },
                    0
                );

            },
            [transactions]
        );


    const totalNonExpense =
        useMemo(
            () => {

                return transactions.reduce(
                    (
                        total,
                        row
                    ) => {

                        const type =
                            String(
                                row.type ||
                                row.transaction_type ||
                                ""
                            ).toLowerCase();


                        if (
                            type ===
                            "non_expense"
                        ) {

                            return (
                                total +
                                numberValue(
                                    row.amount
                                )
                            );

                        }


                        return total;

                    },
                    0
                );

            },
            [transactions]
        );


    /* ==========================================================
       EXPORT CSV
    ========================================================== */

    const exportCSV =
        () => {

            if (
                !filteredTransactions.length
            ) {

                return;

            }


            const header = [

                "SL",

                "Type",

                "Account",

                "Credit / Expense Type",

                "Remarks",

                "Amount",

                "Before",

                "After",

                "Date",

                "Txn ID",

            ];


            const rows =
                filteredTransactions.map(
                    (
                        row,
                        index
                    ) => [

                            index + 1,

                            row.type ||
                            row.transaction_type ||
                            "",

                            row.account_name ||
                            row.account ||
                            "",

                            row.credit_expense_type ||
                            row.expense_type ||
                            "",

                            row.remarks ||
                            "",

                            row.amount ||
                            0,

                            row.before_balance ||
                            row.before ||
                            0,

                            row.after_balance ||
                            row.after ||
                            0,

                            row.transaction_date ||
                            row.date ||
                            "",

                            row.txn_id ||
                            row.transaction_id ||
                            "",

                        ]
                );


            const csv =
                [

                    header,

                    ...rows,

                ]
                    .map(
                        row =>
                            row
                                .map(
                                    value =>
                                        `"${String(value)
                                            .replaceAll(
                                                '"',
                                                '""'
                                            )}"`
                                )
                                .join(",")
                    )
                    .join("\n");


            const blob =
                new Blob(
                    [csv],
                    {
                        type:
                            "text/csv;charset=utf-8;",
                    }
                );


            const url =
                URL.createObjectURL(
                    blob
                );


            const link =
                document.createElement(
                    "a"
                );


            link.href =
                url;

            link.download =
                "accounts-expense-transactions.csv";

            link.click();


            URL.revokeObjectURL(
                url
            );

        };


    /* ==========================================================
       RENDER
    ========================================================== */

    return (

        <div className="ae-page">


            {/* ======================================================
          HEADER
      ====================================================== */}

            <div className="ae-header">

                <div className="ae-header-left">

                    <button
                        type="button"
                        className="ae-back-btn"
                        onClick={() =>
                            window.history.back()
                        }
                    >
                        <FaChevronLeft />

                        Back to Finance Overview
                    </button>


                    <div className="ae-title-row">

                        <div className="ae-title-icon">

                            <FaWallet />

                        </div>


                        <div>

                            <h1>

                                Daily Account

                            </h1>

                            <p>

                                Manual credit, expense and
                                non-expense management

                            </p>

                        </div>

                    </div>

                </div>


                <div className="ae-header-actions">

                    <button
                        type="button"
                        className="ae-header-btn"
                        onClick={
                            loadData
                        }
                    >

                        <FaSyncAlt />

                        Refresh

                    </button>

                </div>

            </div>


            {/* ======================================================
          ALERTS
      ====================================================== */}

            {error && (

                <div className="ae-alert ae-alert-error">

                    {error}

                    <button
                        type="button"
                        onClick={() =>
                            setError("")
                        }
                    >
                        ×
                    </button>

                </div>

            )}


            {success && (

                <div className="ae-alert ae-alert-success">

                    {success}

                    <button
                        type="button"
                        onClick={() =>
                            setSuccess("")
                        }
                    >
                        ×
                    </button>

                </div>

            )}


            {/* ======================================================
          ACCOUNT BALANCE
      ====================================================== */}

            <section className="ae-account-card">

                <div className="ae-section-head">

                    <div className="ae-section-title">

                        <div className="ae-section-icon">

                            <FaWallet />

                        </div>

                        <div>

                            <h2>
                                Account Balance
                            </h2>

                            <p>
                                All active accounts
                            </p>

                        </div>

                    </div>


                    <div className="ae-total-balance">

                        <span>
                            Total Balance
                        </span>

                        <strong>
                            {money(
                                totalBalance
                            )}
                        </strong>

                    </div>

                </div>


                <div className="ae-table-wrap">

                    <table className="ae-account-table">

                        <thead>

                            <tr>

                                <th>
                                    A/C NAME
                                </th>

                                <th>
                                    A/C TYPE
                                </th>

                                <th>
                                    A/C NO
                                </th>

                                <th>
                                    BALANCE
                                </th>

                                <th>
                                    ADD BALANCE
                                </th>

                                <th>
                                    EXPENSE
                                </th>

                                <th>
                                    NON EXPENSE
                                </th>

                            </tr>

                        </thead>


                        <tbody>

                            {loading ? (

                                <tr>

                                    <td
                                        colSpan="7"
                                        className="ae-empty"
                                    >
                                        Loading accounts...
                                    </td>

                                </tr>

                            ) : accounts.length === 0 ? (

                                <tr>

                                    <td
                                        colSpan="7"
                                        className="ae-empty"
                                    >

                                        No accounts found.

                                    </td>

                                </tr>

                            ) : (

                                accounts.map(
                                    account => (

                                        <tr
                                            key={
                                                account.id
                                            }
                                        >

                                            <td>

                                                <div className="ae-account-name">

                                                    <span className="ae-bank-icon">

                                                        <FaWallet />

                                                    </span>

                                                    <strong>

                                                        {
                                                            account.account_name ||
                                                            account.name ||
                                                            "Unnamed Account"
                                                        }

                                                    </strong>

                                                </div>

                                            </td>


                                            <td>

                                                {
                                                    account.account_type ||
                                                    account.type ||
                                                    "-"
                                                }

                                            </td>


                                            <td>

                                                {
                                                    account.account_number ||
                                                    account.account_no ||
                                                    "-"
                                                }

                                            </td>


                                            <td>

                                                <strong className="ae-balance">

                                                    {money(
                                                        account.balance ??
                                                        account.current_balance ??
                                                        0
                                                    )}

                                                </strong>

                                            </td>


                                            <td>

                                                <button
                                                    type="button"
                                                    className="ae-action-btn ae-add-btn"
                                                    onClick={() =>
                                                        openModal(
                                                            "balance",
                                                            account
                                                        )
                                                    }
                                                >

                                                    <FaPlus />

                                                </button>

                                            </td>


                                            <td>

                                                <button
                                                    type="button"
                                                    className="ae-action-btn ae-expense-btn"
                                                    onClick={() =>
                                                        openModal(
                                                            "expense",
                                                            account
                                                        )
                                                    }
                                                >

                                                    <FaArrowDown />

                                                </button>

                                            </td>


                                            <td>

                                                <button
                                                    type="button"
                                                    className="ae-action-btn ae-nonexpense-btn"
                                                    onClick={() =>
                                                        openModal(
                                                            "non-expense",
                                                            account
                                                        )
                                                    }
                                                >

                                                    <FaArrowUp />

                                                </button>

                                            </td>

                                        </tr>

                                    )
                                )

                            )}

                        </tbody>

                    </table>

                </div>

            </section>


            {/* ======================================================
          SUMMARY CARDS
      ====================================================== */}

            <div className="ae-summary-grid">


                <div className="ae-summary-card ae-summary-expense">

                    <div className="ae-summary-icon">

                        <FaReceipt />

                    </div>

                    <div>

                        <span>
                            Expense
                        </span>

                        <strong>
                            {money(
                                totalExpense
                            )}
                        </strong>

                        <small>
                            Total expense transactions
                        </small>

                    </div>

                </div>


                <div className="ae-summary-card ae-summary-nonexpense">

                    <div className="ae-summary-icon">

                        <FaExchangeAlt />

                    </div>

                    <div>

                        <span>
                            Non Expense
                        </span>

                        <strong>
                            {money(
                                totalNonExpense
                            )}
                        </strong>

                        <small>
                            Total non-expense transactions
                        </small>

                    </div>

                </div>


            </div>


            {/* ======================================================
          FILTERS
      ====================================================== */}

            <section className="ae-filter-card">
                <div className="ae-filter-row">
                    <div className="ae-filter-item">

                        <label>
                            FROM DATE
                        </label>

                        <div className="ae-input-icon">

                            <FaCalendarAlt />

                            <input
                                type="date"
                                value={
                                    fromDate
                                }
                                onChange={event =>
                                    setFromDate(
                                        event.target.value
                                    )
                                }
                            />

                        </div>

                    </div>


                    <div className="ae-filter-item">

                        <label>
                            TO DATE
                        </label>

                        <div className="ae-input-icon">

                            <FaCalendarAlt />

                            <input
                                type="date"
                                value={
                                    toDate
                                }
                                onChange={event =>
                                    setToDate(
                                        event.target.value
                                    )
                                }
                            />

                        </div>

                    </div>


                    <div className="ae-filter-item">

                        <label>
                            TYPE
                        </label>

                        <select
                            value={
                                transactionType
                            }
                            onChange={event =>
                                setTransactionType(
                                    event.target.value
                                )
                            }
                        >

                            <option value="all">
                                All
                            </option>

                            <option value="credit">
                                Credit
                            </option>

                            <option value="expense">
                                Expense
                            </option>

                            <option value="non-expense">
                                Non Expense
                            </option>

                        </select>

                    </div>


                    <div className="ae-filter-search">

                        <label>
                            SEARCH
                        </label>

                        <div className="ae-search-box">

                            <FaSearch />

                            <input
                                type="text"
                                value={
                                    search
                                }
                                onChange={event =>
                                    setSearch(
                                        event.target.value
                                    )
                                }
                                placeholder="Search transaction..."
                            />

                        </div>

                    </div>
                    <button
                        type="button"
                        className="ae-filter-apply"
                        onClick={() => {

                            setAppliedFromDate(fromDate);

                            setAppliedToDate(toDate);

                            setAppliedTransactionType(
                                transactionType
                            );

                            setAppliedSearch(search);

                            setCurrentPage(1);

                        }}
                    >
                        <FaFilter />
                        Apply
                    </button>

                    <button
                        type="button"
                        className="ae-filter-reset"
                        onClick={() => {

                            const today =
                                getToday();

                            setFromDate(today);
                            setToDate(today);
                            setTransactionType("all");
                            setSearch("");

                            setAppliedFromDate(today);
                            setAppliedToDate(today);
                            setAppliedTransactionType("all");
                            setAppliedSearch("");

                            setCurrentPage(1);

                        }}
                    >
                        Reset
                    </button>
                </div>
            </section>


            {/* ======================================================
          TRANSACTIONS
      ====================================================== */}

            <section className="ae-transactions-card">

                <div className="ae-transactions-head">

                    <div>

                        <h2>
                            Daily Transactions
                        </h2>

                        <p>
                            Credit, expense and
                            non-expense entries
                        </p>

                    </div>


                    <div className="ae-transaction-actions">

                        <span className="ae-entry-count">

                            {filteredTransactions.length}
                            {" "}
                            Entries

                        </span>


                        <button
                            type="button"
                            className="ae-export-btn"
                            onClick={
                                exportCSV
                            }
                            disabled={
                                !filteredTransactions.length
                            }
                        >

                            <FaDownload />

                            Download

                        </button>

                    </div>

                </div>


                <div className="ae-table-wrap">

                    <table className="ae-transactions-table">

                        <thead>

                            <tr>

                                <th>
                                    SL
                                </th>

                                <th>
                                    TYPE
                                </th>

                                <th>
                                    ACCOUNT
                                </th>

                                <th>
                                    CREDIT / EXPENSE TYPE
                                </th>

                                <th>
                                    REMARKS
                                </th>

                                <th>
                                    AMOUNT
                                </th>

                                <th>
                                    BEFORE
                                </th>

                                <th>
                                    AFTER
                                </th>

                                <th>
                                    DATE
                                </th>

                                <th>
                                    TXN ID
                                </th>

                                <th>
                                    ACTION
                                </th>

                            </tr>

                        </thead>


                        <tbody>

                            {loading ? (

                                <tr>

                                    <td
                                        colSpan="11"
                                        className="ae-empty"
                                    >
                                        Loading transactions...
                                    </td>

                                </tr>

                            ) : visibleTransactions.length === 0 ? (

                                <tr>

                                    <td
                                        colSpan="11"
                                        className="ae-empty"
                                    >

                                        <FaReceipt />

                                        <span>
                                            No transaction found.
                                        </span>

                                    </td>

                                </tr>

                            ) : (

                                visibleTransactions.map(
                                    (
                                        row,
                                        index
                                    ) => {

                                        const type =
                                            String(
                                                row.transaction_type ||
                                                row.type ||
                                                row.entry_type ||
                                                row.transactionType ||
                                                row.txn_type ||
                                                ""
                                            )
                                                .toLowerCase()
                                                .trim()
                                                .replace(/-/g, "_");

                                        const isExpense =
                                            type === "expense";

                                        const isNonExpense =
                                            type === "non_expense" ||
                                            type === "nonexpense";

                                        const isCredit =
                                            type === "credit";

                                        return (

                                            <tr
                                                key={
                                                    row.id ||
                                                    row.txn_id ||
                                                    index
                                                }
                                            >

                                                <td>

                                                    {
                                                        (
                                                            (
                                                                currentPage -
                                                                1
                                                            ) *
                                                            rowsPerPage
                                                        ) +
                                                        index +
                                                        1
                                                    }

                                                </td>


                                                <td>

                                                    <span
                                                        className={
                                                            `ae-type-badge ${isExpense
                                                                ? "expense"
                                                                : isCredit
                                                                    ? "credit"
                                                                    : "nonexpense"
                                                            }`
                                                        }
                                                    >

                                                        {isExpense
                                                            ? "Expense"
                                                            : isCredit
                                                                ? "Credit"
                                                                : "Non Expense"}

                                                    </span>

                                                </td>


                                                <td>

                                                    <strong>

                                                        {
                                                            row.account_name ||
                                                            row.account ||
                                                            "-"
                                                        }

                                                    </strong>

                                                </td>


                                                <td>

                                                    {
                                                        row.credit_type ||
                                                        row.credit_expense_type ||
                                                        row.expense_type ||
                                                        row.category_name ||
                                                        "-"
                                                    }

                                                </td>


                                                <td>

                                                    {
                                                        row.remarks ||
                                                        "-"
                                                    }

                                                </td>


                                                <td>

                                                    <strong
                                                        className={
                                                            isExpense || isNonExpense
                                                                ? "ae-amount-expense"
                                                                : "ae-amount-credit"
                                                        }
                                                    >

                                                        {isExpense || isNonExpense
                                                            ? "-"
                                                            : "+"}

                                                        {money(
                                                            row.amount
                                                        )}

                                                    </strong>

                                                </td>


                                                <td>

                                                    {money(
                                                        row.before_balance ||
                                                        row.before ||
                                                        0
                                                    )}

                                                </td>


                                                <td>

                                                    <strong>

                                                        {money(
                                                            row.after_balance ||
                                                            row.after ||
                                                            0
                                                        )}

                                                    </strong>

                                                </td>


                                                <td>

                                                    {
                                                        row.transaction_date ||
                                                        row.date ||
                                                        "-"
                                                    }

                                                </td>


                                                <td>

                                                    <span className="ae-txn-id">

                                                        {
                                                            row.txn_id ||
                                                            row.transaction_id ||
                                                            "-"
                                                        }

                                                    </span>

                                                </td>


                                                <td>

                                                    <div className="ae-row-actions">

                                                        <button
                                                            type="button"
                                                            className="ae-row-edit"
                                                            onClick={() =>
                                                                openModal(
                                                                    isExpense
                                                                        ? "expense"
                                                                        : isNonExpense
                                                                            ? "non-expense"
                                                                            : "balance",
                                                                    null,
                                                                    row
                                                                )
                                                            }
                                                        >

                                                            <FaEdit />

                                                        </button>


                                                        <button
                                                            type="button"
                                                            className="ae-row-delete"
                                                            onClick={() =>
                                                                setDeleteId(
                                                                    row.id ||
                                                                    row.transaction_id
                                                                )
                                                            }
                                                        >

                                                            <FaTrash />

                                                        </button>

                                                    </div>

                                                </td>

                                            </tr>

                                        );

                                    }
                                )

                            )}

                        </tbody>

                    </table>

                </div>


                {/* PAGINATION */}

                {filteredTransactions.length > 0 && (

                    <div className="ae-pagination">

                        <span>

                            Showing{" "}

                            {
                                (
                                    (
                                        currentPage -
                                        1
                                    ) *
                                    rowsPerPage
                                ) +
                                1
                            }

                            {" "}
                            to{" "}

                            {Math.min(
                                currentPage *
                                rowsPerPage,
                                filteredTransactions.length
                            )}

                            {" "}
                            of{" "}

                            {filteredTransactions.length}

                        </span>


                        <div>

                            <button
                                type="button"
                                disabled={
                                    currentPage <= 1
                                }
                                onClick={() =>
                                    setCurrentPage(
                                        page =>
                                            Math.max(
                                                1,
                                                page - 1
                                            )
                                    )
                                }
                            >

                                <FaChevronLeft />

                            </button>


                            <strong>

                                {currentPage}

                            </strong>


                            <button
                                type="button"
                                disabled={
                                    currentPage >=
                                    totalPages
                                }
                                onClick={() =>
                                    setCurrentPage(
                                        page =>
                                            Math.min(
                                                totalPages,
                                                page + 1
                                            )
                                    )
                                }
                            >

                                <FaChevronRight />

                            </button>

                        </div>

                    </div>

                )}

            </section>


            {/* ======================================================
          TRANSACTION MODAL
      ====================================================== */}

            {modal && (

                <div
                    className="ae-modal-overlay"
                    onMouseDown={event => {

                        if (
                            event.target ===
                            event.currentTarget
                        ) {
                            closeModal();
                        }

                    }}
                >

                    <div className="ae-modal">

                        <div className="ae-modal-head">

                            <div>

                                <span>
                                    {modal === "expense"
                                        ? "EXPENSE"
                                        : modal === "non-expense"
                                            ? "NON EXPENSE"
                                            : "ADD BALANCE"}
                                </span>

                                <h3>

                                    {selectedAccount?.account_name ||
                                        selectedAccount?.name ||
                                        "Account Transaction"}

                                </h3>

                            </div>


                            <button
                                type="button"
                                onClick={
                                    closeModal
                                }
                            >
                                ×
                            </button>

                        </div>


                        <form
                            onSubmit={
                                submitTransaction
                            }
                        >


                            {/* ACCOUNT */}
                            <div className="ae-form-group">

                                <label>ACCOUNT</label>

                                <div className="ae-selected-account">

                                    {selectedAccount?.account_name ||
                                        selectedAccount?.name ||
                                        "Account Transaction"}

                                </div>

                            </div>


                            {/* CREDIT / EXPENSE TYPE */}
                            {modal !== "balance" && (
                                <div className="ae-form-group">

                                    <label>CREDIT / EXPENSE TYPE</label>

                                    <select
                                        name="expense_type"
                                        value={form.expense_type || ""}
                                        onChange={handleChange}
                                        required
                                    >
                                        <option value="">
                                            -- Choose --
                                        </option>

                                        {transactionCategories.map((category) => (
                                            <option
                                                key={category}
                                                value={category}
                                            >
                                                {category}
                                            </option>
                                        ))}
                                    </select>

                                </div>
                            )}


                            <div className="ae-form-group">

                                <label>
                                    AMOUNT
                                </label>

                                <div className="ae-money-input">

                                    <span>
                                        ₹
                                    </span>

                                    <input
                                        type="number"
                                        name="amount"
                                        min="0.01"
                                        step="0.01"
                                        value={
                                            form.amount
                                        }
                                        onChange={
                                            handleChange
                                        }
                                        placeholder="0.00"
                                        required
                                    />

                                </div>

                            </div>
                            {modal === "balance" && (

                                <div className="ae-form-group">

                                    <label>
                                        CREDIT TYPE
                                    </label>

                                    <select
                                        name="credit_type"
                                        value={form.credit_type}
                                        onChange={handleChange}
                                        required
                                    >

                                        <option value="">
                                            Select Credit Type
                                        </option>

                                        {transactionCategories.map(
                                            (category, index) => (

                                                <option
                                                    key={index}
                                                    value={category}
                                                >
                                                    {category}
                                                </option>

                                            )
                                        )}

                                    </select>

                                </div>

                            )}


                            <div className="ae-form-group">

                                <label>
                                    DATE
                                </label>

                                <input
                                    type="date"
                                    name="transaction_date"
                                    value={
                                        form.transaction_date
                                    }
                                    onChange={
                                        handleChange
                                    }
                                    required
                                />

                            </div>


                            <div className="ae-form-group">

                                <label>
                                    REMARKS
                                </label>

                                <textarea
                                    name="remarks"
                                    value={
                                        form.remarks
                                    }
                                    onChange={
                                        handleChange
                                    }
                                    placeholder="Enter remarks..."
                                    rows="4"
                                />

                            </div>


                            <div className="ae-modal-actions">

                                <button
                                    type="button"
                                    className="ae-cancel-btn"
                                    onClick={
                                        closeModal
                                    }
                                    disabled={
                                        saving
                                    }
                                >
                                    Cancel
                                </button>


                                <button
                                    type="submit"
                                    className={
                                        `ae-submit-btn ${modal === "expense"
                                            ? "expense"
                                            : modal === "non-expense"
                                                ? "nonexpense"
                                                : "balance"
                                        }`
                                    }
                                    disabled={
                                        saving
                                    }
                                >

                                    {saving
                                        ? "Saving..."
                                        : "Save Transaction"}

                                </button>

                            </div>

                        </form>

                    </div>

                </div>

            )}


            {/* ======================================================
          DELETE MODAL
      ====================================================== */}

            {deleteId && (

                <div
                    className="ae-modal-overlay"
                >

                    <div className="ae-confirm-modal">

                        <div className="ae-confirm-icon">

                            <FaTrash />

                        </div>

                        <h3>
                            Delete Transaction?
                        </h3>

                        <p>
                            Ei transaction-ti delete
                            korte chaccho. Ei action
                            undo kora jabe na.
                        </p>


                        <div className="ae-modal-actions">

                            <button
                                type="button"
                                className="ae-cancel-btn"
                                onClick={() =>
                                    setDeleteId(
                                        null
                                    )
                                }
                                disabled={
                                    saving
                                }
                            >
                                Cancel
                            </button>


                            <button
                                type="button"
                                className="ae-delete-confirm-btn"
                                onClick={
                                    deleteTransaction
                                }
                                disabled={
                                    saving
                                }
                            >

                                {saving
                                    ? "Deleting..."
                                    : "Delete"}

                            </button>

                        </div>

                    </div>

                </div>

            )}

        </div>

    );

};


export default AccountsExpense;